import { Link } from "react-router-dom";
import SharedLayout from "../components/SharedLayout";
import useSEO from "../utils/useSEO";
import { HeroIllo, WebDevIllo } from "../components/AnimatedIllustrations";

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
    <path d="M5 13l4 4L19 7" />
  </svg>
);
const GlowOrb = ({ className }) => (
  <div className={`absolute rounded-full blur-3xl opacity-20 pointer-events-none ${className}`} />
);

const WHAT_WE_BUILD = [
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
      </svg>
    ),
    title: "Single-Page Applications",
    desc: "Lightning-fast React and Vue SPAs with code-splitting, lazy loading, and optimised bundle sizes for sub-second load times.",
    color: "border-violet-500/30 bg-violet-500/5",
    accent: "text-violet-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M5.25 14.25h13.5m-13.5 0a3 3 0 0 1-3-3m3 3a3 3 0 1 0 0 6h13.5a3 3 0 1 0 0-6m-16.5-3a3 3 0 0 1 3-3h13.5a3 3 0 0 1 3 3m-19.5 0a4.5 4.5 0 0 1 .9-2.7L5.737 5.1a3.375 3.375 0 0 1 2.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 0 1 .9 2.7m0 0a3 3 0 0 1-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
      </svg>
    ),
    title: "Next.js & SSR",
    desc: "Server-side rendered and statically generated sites. Perfect SEO, instant TTFB, and seamless edge deployments via Vercel or Cloudflare.",
    color: "border-slate-500/30 bg-slate-500/5",
    accent: "text-slate-300",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0z" />
      </svg>
    ),
    title: "E-Commerce Platforms",
    desc: "Headless commerce with Shopify, custom storefronts, payment integrations (Stripe, Braintree), and conversion-optimised checkout flows.",
    color: "border-emerald-500/30 bg-emerald-500/5",
    accent: "text-emerald-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" />
      </svg>
    ),
    title: "Enterprise Portals",
    desc: "Role-based dashboards, complex form workflows, real-time data tables, and deep API integrations for internal operations teams.",
    color: "border-cyan-500/30 bg-cyan-500/5",
    accent: "text-cyan-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
      </svg>
    ),
    title: "Progressive Web Apps",
    desc: "Installable, offline-capable PWAs with service workers and push notifications — native-app feel, web-app reach.",
    color: "border-rose-500/30 bg-rose-500/5",
    accent: "text-rose-400",
  },
  {
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125z" />
      </svg>
    ),
    title: "Data Dashboards",
    desc: "Real-time analytics UIs, interactive charts, live WebSocket feeds, and drill-down reporting interfaces.",
    color: "border-amber-500/30 bg-amber-500/5",
    accent: "text-amber-400",
  },
];

const TECH = [
  "React", "Next.js", "TypeScript", "Vue.js", "Tailwind CSS", "GraphQL",
  "REST APIs", "Vite", "Webpack", "Jest / RTL", "Storybook", "Figma",
];

const PROCESS = [
  {
    step: "01",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
      </svg>
    ),
    title: "Discovery & Scoping",
    desc: "We audit your requirements, map user journeys, and define the tech stack and architecture before any code is written.",
  },
  {
    step: "02",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M9.53 16.122a3 3 0 0 0-5.78 1.128 2.25 2.25 0 0 1-2.4 2.245 4.5 4.5 0 0 0 8.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 0 0 3.388-1.62m-5.043-.025a15.994 15.994 0 0 1 1.622-3.395m3.42 3.42a15.995 15.995 0 0 0 4.764-4.648l3.876-5.814a1.151 1.151 0 0 0-1.597-1.597L14.146 6.32a15.996 15.996 0 0 0-4.649 4.763m3.42 3.42a6.776 6.776 0 0 0-3.42-3.42" />
      </svg>
    ),
    title: "Design & Prototyping",
    desc: "High-fidelity Figma designs, interactive prototypes, and a shared design system before engineering begins.",
  },
  {
    step: "03",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M14.25 9.75L16.5 12l-2.25 2.25m-4.5 0L7.5 12l2.25-2.25M6 20.25h12A2.25 2.25 0 0 0 20.25 18V6A2.25 2.25 0 0 0 18 3.75H6A2.25 2.25 0 0 0 3.75 6v12A2.25 2.25 0 0 0 6 20.25z" />
      </svg>
    ),
    title: "Agile Development",
    desc: "Two-week sprints with deployed previews every cycle. You review real builds, not screenshots.",
  },
  {
    step: "04",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 1-6.23-.693L4.2 13.9m15.6 1.4L21 18m-1.2-2.7-.566-.141M4.2 13.9 3 18m1.2-4.1.566-.141M12 12a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
      </svg>
    ),
    title: "QA & Performance",
    desc: "Unit, integration, and E2E testing (Playwright). Lighthouse audits, Core Web Vitals, and cross-browser checks.",
  },
  {
    step: "05",
    icon: () => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6">
        <path d="M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
      </svg>
    ),
    title: "Launch & Monitoring",
    desc: "Zero-downtime deployment, error monitoring (Sentry), analytics wiring, and post-launch iteration.",
  },
];

const FEATURES = [
  "Core Web Vitals optimised (LCP, CLS, FID)",
  "SEO-ready HTML structure & meta tags",
  "Accessibility (WCAG 2.1 AA) by default",
  "Responsive across all screen sizes",
  "Authentication & role-based access",
  "Internationalisation (i18n) & RTL",
  "Dark / light mode support",
  "CI/CD pipeline with preview deployments",
  "Error monitoring & alerting",
  "Analytics & event tracking",
  "End-to-end test suite",
  "Design handoff documentation",
];

export default function WebDevDetail() {
  useSEO({
    title: "Web App Development Agency | React & Next.js | BerryByte",
    description: "Custom web application development using React, Next.js & TypeScript. BerryByte builds scalable, SEO-optimized web apps for US & Canadian startups.",
    keywords: "custom web application development, React development agency, Next.js development company, web development services US Canada, enterprise web portal development, SPA development agency",
    canonical: "https://www.berrybyteinn.com/services/web-development",
  });

  return (
    <SharedLayout>
      <section className="relative min-h-[70vh] flex items-center overflow-hidden pt-20">
        <GlowOrb className="w-[700px] h-[700px] bg-violet-600 -top-32 -left-40" />
        <GlowOrb className="w-[500px] h-[500px] bg-cyan-700 bottom-0 -right-32" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "80px 80px" }} />

        {/* decorative animated illustration */}
        <HeroIllo>
          <WebDevIllo />
        </HeroIllo>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24">
          <a href="/services" className="inline-flex items-center gap-2 text-slate-500 hover:text-violet-400 text-sm font-medium transition-colors duration-200 mb-8">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
            Services / Web Development
          </a>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 text-sm font-medium mb-6 backdrop-blur-sm">
              <span className="w-2 h-2 rounded-full bg-violet-400 animate-pulse" />
              React · Next.js · TypeScript · and more
            </div>

            <h1 className="text-5xl lg:text-7xl font-black text-white leading-tight mb-6">
              Web Apps Built<br />
              <span className="bg-gradient-to-r from-violet-400 via-indigo-400 to-cyan-400 bg-clip-text text-transparent">to Scale.</span>
            </h1>

            <p className="text-xl text-slate-400 leading-relaxed max-w-2xl mb-10">
              From marketing sites to mission-critical enterprise platforms — we craft web experiences that are fast, accessible, and built to last. Pixel-perfect UI, rock-solid engineering optimised for US & Canadian markets.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold text-base shadow-2xl shadow-violet-900/40 hover:shadow-violet-900/60 hover:-translate-y-1 transition-all duration-300">
                Start Your Project
                <span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8 pt-12 border-t border-white/5">
              {[{ value: "80+", label: "Web Projects" }, { value: "98", label: "Avg Lighthouse Score" }, { value: "<1s", label: "Target Load Time" }, { value: "5yrs", label: "React Experience" }].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="text-2xl lg:text-3xl font-black text-white mb-1">{s.value}</div>
                  <div className="text-sm text-slate-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[500px] h-[500px] bg-violet-700 -top-24 -right-32" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-violet-400 font-semibold text-sm tracking-widest uppercase">What We Build</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">Every type of web product,<br /><span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">done properly.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHAT_WE_BUILD.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className={`rounded-2xl border ${item.color} p-7 hover:-translate-y-1 transition-transform duration-300`}>
                  <div className={`inline-flex p-3 rounded-xl bg-white/5 border border-white/10 ${item.accent} mb-4`}><Icon /></div>
                  <h3 className={`text-base font-bold mb-2 ${item.accent}`}>{item.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#050810] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase">How We Work</span>
            <h2 className="mt-4 text-4xl lg:text-5xl font-black text-white">From brief to launch.<br /><span className="bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">No surprises.</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PROCESS.map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.step} className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 hover:-translate-y-1 transition-transform duration-300">
                  <div className="inline-flex p-2 rounded-lg bg-violet-500/10 border border-violet-500/20 text-violet-400 mb-4"><Icon /></div>
                  <div className="text-xs font-bold text-violet-500 tracking-widest mb-2">{step.step}</div>
                  <h3 className="text-sm font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-slate-400 text-xs leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-violet-400 font-semibold text-sm tracking-widest uppercase">Technology</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">Our front-end stack.</h2>
              <div className="flex flex-wrap gap-3">
                {TECH.map((t) => (
                  <span key={t} className="px-4 py-2 rounded-xl border border-violet-500/20 bg-violet-500/5 text-violet-300 text-sm font-medium hover:bg-violet-500/10 transition-colors">
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase">What's Included</span>
              <h2 className="mt-4 text-3xl lg:text-4xl font-black text-white mb-8">Built in, not bolted on.</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {FEATURES.map((f) => (
                  <div key={f} className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-400"><Check /></span>
                    <span className="text-slate-300 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#070b14] overflow-hidden">
        <GlowOrb className="w-[700px] h-[700px] bg-violet-700 top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-6xl font-black text-white mb-6">
            Have a web project?<br />
            <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">Let's talk.</span>
          </h2>
          <p className="text-slate-400 text-lg mb-10 leading-relaxed">
            Book a free 30-minute discovery call. We'll review your requirements and give you an honest assessment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-600 text-white font-semibold shadow-2xl shadow-violet-900/40 hover:shadow-violet-900/60 hover:-translate-y-1 transition-all duration-300">
              Get a Free Consultation<span className="group-hover:translate-x-1 transition-transform duration-200"><ArrowRight /></span>
            </Link>
            <a href="/services" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-white/10 text-white font-semibold bg-white/5 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300">
              Explore Other Services
            </a>
          </div>
        </div>
      </section>
    </SharedLayout>
  );
}
